import React from "react";
import { DocumentNode } from "@apollo/client";
import { List, message, Spin, Input } from "antd";
import type { ListProps, InputProps } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import { client } from "../App";

const { Search } = Input;
type SearchProps = InputProps;

type InfiniteListProps = {
  query: DocumentNode;
  variables?: {};
  renderListItem: (record: any, index: number) => React.ReactNode;
  recordKey: string;
  perPage: number;
  showSearch?: boolean;
  searchProps?: SearchProps;
  onSearchChange?: (searchString: string) => void;
  listProps?: ListProps<any>;
  pollInterval?: number;
};

export default class InfiniteList extends React.Component<InfiniteListProps> {
  state = {
    totalCount: 1,
    data: [],
    loading: false,
    hasMore: true,
    offset: 0,
    limit: this.props.perPage,
    queryString: null,
    lastVariables: {},
  };

  componentDidMount() {
    this.updateData(() => {
      if (this.props.pollInterval) {
        setTimeout(() => {
          this.startPolling();
        }, this.props.pollInterval);
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps !== this.props) {
      this.setState({ data: [], offset: 0 }, () => {
        this.updateData();
      });
    }
  }

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newQueryString = event.target.value;
    this.handleSearch(newQueryString);
  };

  handleSearch = (newQueryString: string) => {
    let { queryString } = this.state;
    if (newQueryString != queryString) {
      if (this.props.onSearchChange) {
        this.props.onSearchChange(newQueryString);
      }
      this.setState(
        { data: [], offset: 0, queryString: newQueryString },
        () => {
          this.updateData();
        },
      );
    }
  };

  fetchData = async (
    callback: (
      newData: any[],
      totalCount: number,
      newOffset: number,
      variables: any,
    ) => any,
  ) => {
    let { offset, limit, queryString } = this.state;

    const variables = {
      offset: offset,
      limit: limit,
      queryString: queryString,
      ...this.props.variables,
    };
    const { data } = await client.query({
      query: this.props.query,
      variables: variables,
    });
    const response = data[Object.keys(data)[0]];
    const newOffset = offset + limit;

    callback(
      response[this.props.recordKey],
      response.totalCount,
      newOffset,
      variables,
    );
  };

  updateData = (callback?: () => void) => {
    const { data } = this.state;
    this.setState({ loading: true }, () => {
      this.fetchData((newData, totalCount, offset, variables) => {
        this.setState(
          {
            data: data.concat(newData),
            totalCount: totalCount,
            offset: offset,
            lastVariables: variables,
            loading: false,
            hasMore: true,
          },
          () => {
            if (callback) {
              callback();
            }
          },
        );
      });
    });
  };

  startPolling = async () => {
    if (!this.props.pollInterval) return;

    let variables: any = this.state.lastVariables;
    variables.limit = variables.offset + variables.limit;
    variables.offset = 0;

    const { data } = await client.query({
      query: this.props.query,
      variables: variables,
      fetchPolicy: "network-only",
    });

    const response = data[Object.keys(data)[0]];
    const newData = response[this.props.recordKey];

    this.setState({ data: newData });

    setTimeout(this.startPolling, this.props.pollInterval);
  };

  handleInfiniteOnLoad = () => {
    let { data, totalCount, limit } = this.state;
    if (data.length >= totalCount) {
      if (totalCount > limit / 2) message.warning("All records loaded");
      this.setState({
        hasMore: false,
        loading: false,
      });
      return;
    }
    this.updateData();
  };

  searchInput = () => {
    if (!this.props.showSearch) return;
    return (
      <Search
        placeholder="input search text"
        onSearch={this.handleSearch}
        onChange={this.handleSearchChange}
        size="middle"
        style={{
          width: "25%",
          float: "right",
          zIndex: 1,
          position: "relative",
        }}
        allowClear={true}
        {...this.props.searchProps}
      />
    );
  };

  render() {
    return (
      <div className="demo-infinite-container">
        {this.searchInput()}
        <InfiniteScroll
          dataLength={this.state.data.length}
          next={this.handleInfiniteOnLoad}
          hasMore={!this.state.loading && this.state.hasMore}
          loader={null}
        >
          <List
            dataSource={this.state.data}
            renderItem={this.props.renderListItem}
            {...this.props.listProps}
          >
            {this.state.loading && this.state.hasMore && (
              <div className="demo-loading-container">
                <Spin />
              </div>
            )}
          </List>
        </InfiniteScroll>
      </div>
    );
  }
}
